/**
 * Darstellung einer abgegebenen Bewertungsantwort — ohne React, damit die
 * Regeln ohne Rendering prüfbar sind.
 *
 * **Warum das nötig ist.** `RatingAnswer` speichert nur die Zahl; die Skala
 * steht an der Frage. Wird der Bogen später bearbeitet — und genau dazu
 * fordert der QS-Testfall 9 auf („0, 1, 3, 10 und 80 eintragen") —, passt eine
 * bereits gegebene Antwort nicht mehr zur heutigen Skala.
 *
 * Die Detailansicht malte in diesem Fall trotzdem Sterne: bei Wert 10 und
 * Skala 5 fünf gefüllte Sterne mit der Beschriftung „10/5" (qs/dino-qs#9,
 * TF13). Das ist eine unmögliche Angabe, und sie verschleiert die eigentliche
 * Ursache. Hier wird deshalb entschieden, wann eine Sternedarstellung
 * überhaupt zulässig ist — und wenn nicht, sagt die Anzeige warum.
 *
 * Die vollständige Behebung wäre, die Skala **mit der Antwort** zu speichern.
 * Solange das nicht geschieht, ist die Anzeige wenigstens ehrlich statt falsch.
 */

/** Mehr Symbole sprengen die Zeile — darüber wird rein numerisch angezeigt. */
export const MAX_STAR_ICONS = 10;

export type AnswerDisplayKind = 'stars' | 'number' | 'yesno' | 'text' | 'none';

/** Warum die Darstellung von der erwarteten abweicht. */
export type AnswerNote = 'ok' | 'scale-unknown' | 'value-out-of-range';

export interface IAnswerDisplay {
  kind: AnswerDisplayKind;
  value: number | null;
  /** Obergrenze der Frage, `null` wenn unbekannt. */
  total: number | null;
  text: string | null;
  note: AnswerNote;
}

export interface IAnswerLike {
  QuestionType?: string | null;
  AnswerNumeric?: number | null;
  AnswerText?: string | null;
  RangeMax?: number | null;
}

/** Zahl aus einem Wert, der auch als String ankommen kann (DECIMAL-Spalten
 *  liefern je nach Weg „4.00"). `null`, wenn nichts Brauchbares vorliegt. */
const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const NOTHING: IAnswerDisplay = {
  kind: 'none',
  value: null,
  total: null,
  text: null,
  note: 'ok',
};

/** Wie eine Antwort dargestellt wird. */
export const answerDisplay = (answer: IAnswerLike): IAnswerDisplay => {
  const value = toNumber(answer.AnswerNumeric);
  const rangeMax = toNumber(answer.RangeMax);
  const total = rangeMax !== null && rangeMax > 0 ? rangeMax : null;
  const type = answer.QuestionType ?? '';

  if (type === 'yesno' && value !== null) {
    return { ...NOTHING, kind: 'yesno', value };
  }

  if ((type === 'stars' || type === 'scale') && value !== null) {
    if (total === null) {
      return { ...NOTHING, kind: 'number', value, note: 'scale-unknown' };
    }
    if (value > total) {
      // Der Wert kann auf dieser Skala nicht zustande gekommen sein.
      return {
        ...NOTHING,
        kind: 'number',
        value,
        total,
        note: 'value-out-of-range',
      };
    }
    // Skalen zeigen Zahlen, Sterne Symbole — solange es nicht zu viele sind.
    const asStars = type === 'stars' && total <= MAX_STAR_ICONS;
    return { ...NOTHING, kind: asStars ? 'stars' : 'number', value, total };
  }

  const text = (answer.AnswerText ?? '').trim();
  if (text)
    return { ...NOTHING, kind: 'text', text: answer.AnswerText ?? null };

  // Eine Zahl ohne bekannten Fragetyp (gelöschte Frage, Altdaten) ist immer
  // noch eine Information — sie zu verschweigen wäre der grössere Fehler.
  if (value !== null) {
    return { ...NOTHING, kind: 'number', value, note: 'scale-unknown' };
  }
  return NOTHING;
};

/** Erläuterung zu einer abweichenden Darstellung, oder `null`. */
export const answerNoteText = (
  note: AnswerNote,
  value: number | null,
  total: number | null
): string | null => {
  if (note === 'value-out-of-range') {
    return `Die Antwort lautet ${value}, die Frage hat heute aber nur ${total} Stufen. Die Skala der Frage wurde nach der Abgabe geändert — die Antwort selbst ist unverändert.`;
  }
  if (note === 'scale-unknown') {
    return 'Zu dieser Frage ist keine Skala hinterlegt, der Wert lässt sich deshalb nicht einordnen.';
  }
  return null;
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'In Vorbereitung',
  sent: 'Versendet',
  opened: 'Geöffnet',
  submitted: 'Abgegeben',
  expired: 'Abgelaufen',
  failed: 'Fehler',
};

/** Status einer Bewertungsantwort in Klartext. Ein unbekannter Status wird im
 *  Rohtext gezeigt — stillschweigend zu übergehen wäre schlimmer. */
export const ratingStatusLabel = (status?: string | null): string => {
  if (!status) return STATUS_LABEL.pending;
  return STATUS_LABEL[status] ?? status;
};

export type RatingChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

const STATUS_COLOR: Record<string, RatingChipColor> = {
  pending: 'default',
  sent: 'info',
  opened: 'primary',
  submitted: 'success',
  expired: 'warning',
  failed: 'error',
};

export const ratingStatusColor = (status?: string | null): RatingChipColor =>
  (status && STATUS_COLOR[status]) || 'default';
